/**
 * Marketplace Products List Page
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  IconButton,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { PageHeader, DataTable, Column, ConfirmDialog } from '../../components/common';
import {
  useGetMarketplaceProductsQuery,
  useDeleteMarketplaceProductMutation,
  useGetMarketplaceCategoriesQuery,
  useGetMarketplaceSubCategoriesQuery,
} from '../../store/api';
import { MarketplaceProductListItem, StockStatus } from '../../types/marketplace';
import { formatCurrency } from '../../utils/pricing';
import toast from 'react-hot-toast';

const STOCK_STATUS_COLORS: Record<StockStatus, 'success' | 'error' | 'warning'> = {
  IN_STOCK: 'success',
  OUT_OF_STOCK: 'error',
  PREORDER: 'warning',
};

export const ProductsListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<number | ''>('');
  const [subcategoryFilter, setSubcategoryFilter] = useState<number | ''>('');
  const [pricingTypeFilter, setPricingTypeFilter] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<string>('');
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selected, setSelected] = useState<MarketplaceProductListItem | null>(null);

  const { data, isLoading, error } = useGetMarketplaceProductsQuery({
    page: page + 1,
    page_size: rowsPerPage,
    search: search || undefined,
    category: categoryFilter ? Number(categoryFilter) : undefined,
    subcategory: subcategoryFilter ? Number(subcategoryFilter) : undefined,
    pricing_type: pricingTypeFilter || undefined,
    is_active: activeFilter === 'active' ? true : activeFilter === 'inactive' ? false : undefined,
  });

  const { data: categoriesData } = useGetMarketplaceCategoriesQuery({ page_size: 100 });
  const { data: subcategoriesData } = useGetMarketplaceSubCategoriesQuery({
    page_size: 100,
    category: categoryFilter ? Number(categoryFilter) : undefined,
  });

  const [deleteProduct, { isLoading: isDeleting }] = useDeleteMarketplaceProductMutation();

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await deleteProduct(selected.id).unwrap();
      toast.success(t('marketplaceCatalog.products.toast.deleted'));
      setDeleteDialog(false);
      setSelected(null);
    } catch (err: any) {
      toast.error(err?.data?.message || t('marketplaceCatalog.products.toast.deleteError'));
    }
  };

  const columns: Column<MarketplaceProductListItem>[] = [
    {
      id: 'main_image',
      label: t('marketplaceCatalog.cols.image'),
      minWidth: 80,
      render: (row) => (
        <Avatar src={row.main_image || undefined} alt={row.name} variant="rounded" sx={{ width: 44, height: 44 }}>
          <ImageIcon />
        </Avatar>
      ),
    },
    {
      id: 'name',
      label: t('marketplaceCatalog.cols.name'),
      minWidth: 160,
      render: (row) => (
        <Box
          onClick={() => navigate(`/marketplace/products/${row.id}`)}
          sx={{ fontWeight: 600, color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
        >
          {row.name}
        </Box>
      ),
    },
    {
      id: 'category_name',
      label: t('marketplaceCatalog.cols.category'),
      minWidth: 130,
    },
    {
      id: 'subcategory_name',
      label: t('marketplaceCatalog.cols.subcategory'),
      minWidth: 130,
    },
    {
      id: 'pricing_type',
      label: t('marketplaceCatalog.cols.pricing'),
      minWidth: 110,
      render: (row) => <Chip label={t(`marketplaceCatalog.pricingType.${row.pricing_type}`)} size="small" color="info" />,
    },
    {
      id: 'buy_price',
      label: t('marketplaceCatalog.cols.buyPrice'),
      minWidth: 110,
      align: 'right',
      render: (row) => formatCurrency(row.buy_price),
    },
    {
      id: 'daily_rental_price',
      label: t('marketplaceCatalog.cols.dailyRent'),
      minWidth: 110,
      align: 'right',
      render: (row) => formatCurrency(row.daily_rental_price),
    },
    {
      id: 'stock_status',
      label: t('marketplaceCatalog.cols.stock'),
      minWidth: 110,
      render: (row) => {
        // A product flagged IN_STOCK but with no units on any path it actually
        // sells through is effectively out of stock — show what a customer would
        // see. Derived from the quantities rather than the server's `is_in_stock`
        // so the badge stays correct even against an API that omits that field.
        const isBuyable = row.pricing_type === 'BUY_ONLY' || row.pricing_type === 'RENT_AND_BUY';
        const isRentable = row.pricing_type === 'RENT_ONLY' || row.pricing_type === 'RENT_AND_BUY';
        const hasUnits =
          (isBuyable && row.sale_stock_quantity > 0) ||
          (isRentable && row.rental_stock_quantity > 0);
        const status: StockStatus =
          row.stock_status === 'IN_STOCK' && !hasUnits ? 'OUT_OF_STOCK' : row.stock_status;
        return (
          <Chip
            label={t(`marketplaceCatalog.stockStatus.${status}`)}
            size="small"
            color={STOCK_STATUS_COLORS[status]}
          />
        );
      },
    },
    {
      id: 'rating_average',
      label: t('marketplaceCatalog.cols.rating'),
      minWidth: 90,
      align: 'center',
      render: (row) => `${parseFloat(row.rating_average || '0').toFixed(1)} (${row.reviews_count})`,
    },
    {
      id: 'is_active',
      label: t('marketplaceCatalog.cols.status'),
      minWidth: 100,
      render: (row) => (
        <Chip
          label={row.is_active ? t('marketplaceCatalog.status.active') : t('marketplaceCatalog.status.inactive')}
          size="small"
          color={row.is_active ? 'success' : 'default'}
        />
      ),
    },
    {
      id: 'actions',
      label: t('marketplaceCatalog.cols.actions'),
      minWidth: 150,
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton size="small" onClick={() => navigate(`/marketplace/products/${row.id}`)} color="info" title={t('marketplaceCatalog.buttons.view')}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => navigate(`/marketplace/products/${row.id}/edit`)} color="primary" title={t('marketplaceCatalog.buttons.edit')}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => {
              setSelected(row);
              setDeleteDialog(true);
            }}
            color="error"
            title={t('marketplaceCatalog.buttons.delete')}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={t('marketplaceCatalog.products.title')}
        subtitle={t('marketplaceCatalog.products.subtitle')}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.marketplace'), path: '/marketplace/categories' },
          { label: t('nav.products') },
        ]}
        actionLabel={t('marketplaceCatalog.products.add')}
        onAction={() => navigate('/marketplace/products/create')}
      />

      {/* Filters */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder={t('marketplaceCatalog.products.searchPlaceholder')}
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>{t('marketplaceCatalog.filters.category')}</InputLabel>
            <Select
              value={categoryFilter}
              label={t('marketplaceCatalog.filters.category')}
              onChange={(e) => {
                setCategoryFilter(e.target.value as number | '');
                setSubcategoryFilter('');
                setPage(0);
              }}
            >
              <MenuItem value="">{t('marketplaceCatalog.filters.allCategories')}</MenuItem>
              {categoriesData?.results?.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>{t('marketplaceCatalog.filters.subcategory')}</InputLabel>
            <Select
              value={subcategoryFilter}
              label={t('marketplaceCatalog.filters.subcategory')}
              onChange={(e) => {
                setSubcategoryFilter(e.target.value as number | '');
                setPage(0);
              }}
            >
              <MenuItem value="">{t('marketplaceCatalog.filters.allSubcategories')}</MenuItem>
              {subcategoriesData?.results?.map((subcategory) => (
                <MenuItem key={subcategory.id} value={subcategory.id}>
                  {subcategory.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('marketplaceCatalog.filters.pricingType')}</InputLabel>
            <Select
              value={pricingTypeFilter}
              label={t('marketplaceCatalog.filters.pricingType')}
              onChange={(e) => {
                setPricingTypeFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">{t('marketplaceCatalog.filters.all')}</MenuItem>
              <MenuItem value="BUY_ONLY">{t('marketplaceCatalog.pricingType.BUY_ONLY')}</MenuItem>
              <MenuItem value="RENT_ONLY">{t('marketplaceCatalog.pricingType.RENT_ONLY')}</MenuItem>
              <MenuItem value="RENT_AND_BUY">{t('marketplaceCatalog.pricingType.RENT_AND_BUY')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>{t('marketplaceCatalog.filters.status')}</InputLabel>
            <Select
              value={activeFilter}
              label={t('marketplaceCatalog.filters.status')}
              onChange={(e) => {
                setActiveFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">{t('marketplaceCatalog.filters.all')}</MenuItem>
              <MenuItem value="active">{t('marketplaceCatalog.filters.active')}</MenuItem>
              <MenuItem value="inactive">{t('marketplaceCatalog.filters.inactive')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Card>

      {/* Table */}
      <DataTable
        columns={columns}
        data={data?.results || []}
        loading={isLoading}
        error={error ? t('marketplaceCatalog.products.loadError') : null}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={data?.count || 0}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        emptyMessage={t('marketplaceCatalog.products.empty')}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog}
        title={t('marketplaceCatalog.products.deleteTitle')}
        message={t('marketplaceCatalog.dialog.deleteMessage', { name: selected?.name })}
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteDialog(false);
          setSelected(null);
        }}
        loading={isDeleting}
      />
    </Box>
  );
};

export default ProductsListPage;
